import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Flame,
  Zap,
  Target,
  BookOpen,
  TrendingUp,
  Award,
  Sparkles,
} from 'lucide-react';
import { fetchDailyStats } from '../api/progress';
import type { DailyStats } from '../types';
import { format, subDays, isSameDay } from 'date-fns';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } },
};

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DailyStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDailyStats()
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats) {
    return <DashboardSkeleton />;
  }

  const totalWords = stats.new_count + stats.learning_count + stats.mastered_count;
  const masteryPercent = totalWords > 0 ? (stats.mastered_count / totalWords) * 100 : 0;
  const levelTitle = getLevelTitle(stats.level);

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="p-4 md:p-8 pb-24 md:pb-8 max-w-5xl mx-auto space-y-5"
    >
      {/* Top row: stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <motion.div variants={item} className="glass-card p-4 rounded-2xl">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={16} className="text-gold" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-text-dim">Level {stats.level}</span>
          </div>
          <p className="font-display text-xl text-cream">{stats.xp} <span className="text-sm text-text font-sans">XP</span></p>
          <p className="text-xs text-text-dim mt-0.5">{levelTitle}</p>
        </motion.div>

        <motion.div variants={item} className="glass-card p-4 rounded-2xl">
          <div className="flex items-center gap-2 mb-2">
            <Flame size={16} className="text-amber" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-text-dim">Streak</span>
          </div>
          <p className="font-display text-xl text-cream">{stats.current_streak} <span className="text-sm text-text font-sans">days</span></p>
          <p className="text-xs text-text-dim mt-0.5">Best: {stats.best_streak}</p>
        </motion.div>

        <motion.div variants={item} className="glass-card p-4 rounded-2xl">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen size={16} className="text-green" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-text-dim">Queue</span>
          </div>
          <p className="font-display text-xl text-cream">{stats.review_queue_count} <span className="text-sm text-text font-sans">words</span></p>
          <p className="text-xs text-text-dim mt-0.5">Due today</p>
        </motion.div>

        <motion.div variants={item} className="glass-card p-4 rounded-2xl">
          <div className="flex items-center gap-2 mb-2">
            <Target size={16} className="text-blue" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-text-dim">Weekly Goal</span>
          </div>
          <p className="font-display text-xl text-cream">{stats.days_studied_this_week}<span className="text-sm text-text font-sans">/{stats.weekly_goal_days}</span></p>
          <p className="text-xs text-text-dim mt-0.5">days studied</p>
        </motion.div>
      </div>

      {/* CTA + Mastery Ring */}
      <div className="grid md:grid-cols-3 gap-4">
        <motion.div variants={item} className="md:col-span-2 glass-card p-6 rounded-2xl flex flex-col justify-between">
          <div>
            <h2 className="font-display text-2xl text-cream mb-1">Ready to study?</h2>
            <p className="text-text text-sm mb-6">
              You have <span className="text-gold-text font-semibold">{stats.review_queue_count}</span> words due for review today.
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => navigate('/study')}
              className="btn-gold px-6 py-3 rounded-xl text-sm font-semibold flex items-center gap-2"
            >
              <Sparkles size={16} />
              Start Daily Review
            </button>
            <button
              onClick={() => navigate('/quiz')}
              className="px-6 py-3 rounded-xl text-sm font-medium border border-border bg-surface text-text-hi hover:bg-surface-1 transition-all duration-150 flex items-center gap-2"
            >
              <TrendingUp size={16} />
              Practice Quiz
            </button>
          </div>
        </motion.div>

        <motion.div variants={item} className="glass-card p-6 rounded-2xl flex flex-col items-center justify-center">
          <MasteryRing
            newCount={stats.new_count}
            learningCount={stats.learning_count}
            masteredCount={stats.mastered_count}
          />
          <p className="text-xs text-text-dim mt-3 uppercase tracking-widest font-medium">Mastery</p>
          <p className="font-mono text-lg text-cream mt-0.5">{masteryPercent.toFixed(1)}%</p>
        </motion.div>
      </div>

      {/* Heatmap + Weak Areas + Achievements */}
      <div className="grid md:grid-cols-2 gap-4">
        <motion.div variants={item} className="glass-card p-5 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-text-hi flex items-center gap-2">
              <TrendingUp size={14} className="text-gold" />
              Study Heatmap
            </h3>
            <span className="text-[10px] text-text-dim uppercase tracking-widest">Last 30 days</span>
          </div>
          <HeatmapCalendar heatmap={stats.heatmap} />
        </motion.div>

        <div className="space-y-4">
          <motion.div variants={item} className="glass-card p-5 rounded-2xl">
            <h3 className="text-sm font-semibold text-text-hi flex items-center gap-2 mb-3">
              <Target size={14} className="text-amber" />
              Weak Areas
            </h3>
            {stats.weak_areas.length === 0 ? (
              <p className="text-sm text-text-dim py-2">No weak areas detected yet. Keep studying!</p>
            ) : (
              <div className="space-y-2">
                {stats.weak_areas.slice(0, 5).map((area) => (
                  <div key={area.tag} className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-amber" />
                      <span className="text-sm text-text">{area.tag}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <div
                          className="h-full rounded-full bg-amber"
                          style={{ width: `${area.accuracy}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono text-text-dim w-8 text-right">{area.accuracy}%</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div variants={item} className="glass-card p-5 rounded-2xl">
            <h3 className="text-sm font-semibold text-text-hi flex items-center gap-2 mb-3">
              <Award size={14} className="text-green" />
              Recent Achievements
            </h3>
            {stats.recent_achievements.length === 0 ? (
              <p className="text-sm text-text-dim py-2">Complete sessions to earn achievements!</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {stats.recent_achievements.map((ach) => (
                  <span
                    key={ach.badge_key}
                    className="px-3 py-1 rounded-full text-xs font-medium border border-gold-dim text-gold-text"
                    style={{ background: 'var(--gold-glow)' }}
                  >
                    {ach.badge_key.replace(/-/g, ' ')}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

const MasteryRing: React.FC<{ newCount: number; learningCount: number; masteredCount: number }> = ({
  newCount,
  learningCount,
  masteredCount,
}) => {
  const total = newCount + learningCount + masteredCount || 1;
  const r = 36;
  const c = 2 * Math.PI * r;

  const newOffset = c - (newCount / total) * c;
  const learningOffset = c - (learningCount / total) * c;
  const masteredOffset = c - (masteredCount / total) * c;

  return (
    <svg width="100" height="100" viewBox="0 0 100 100" className="-rotate-90">
      <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
      <circle cx="50" cy="50" r={r} fill="none" stroke="#3e4260" strokeWidth="8"
        strokeDasharray={c} strokeDashoffset={newOffset} strokeLinecap="round"
      />
      <circle cx="50" cy="50" r={r} fill="none" stroke="#e09050" strokeWidth="8"
        strokeDasharray={c} strokeDashoffset={learningOffset} strokeLinecap="round"
        style={{ transform: `rotate(${(newCount / total) * 360}deg)`, transformOrigin: '50% 50%' }}
      />
      <circle cx="50" cy="50" r={r} fill="none" stroke="#3db48a" strokeWidth="8"
        strokeDasharray={c} strokeDashoffset={masteredOffset} strokeLinecap="round"
        style={{ transform: `rotate(${((newCount + learningCount) / total) * 360}deg)`, transformOrigin: '50% 50%' }}
      />
    </svg>
  );
};

const HeatmapCalendar: React.FC<{ heatmap: { date: string; xp: number }[] }> = ({ heatmap }) => {
  const days: { date: Date; xp: number }[] = [];
  for (let i = 29; i >= 0; i--) {
    const date = subDays(new Date(), i);
    const found = heatmap.find((h) => isSameDay(new Date(h.date), date));
    days.push({ date, xp: found?.xp || 0 });
  }

  const getIntensity = (xp: number) => {
    if (xp === 0) return 'rgba(255,255,255,0.03)';
    if (xp <= 50) return 'rgba(108,99,255,0.15)';
    if (xp <= 150) return 'rgba(108,99,255,0.35)';
    return 'rgba(108,99,255,0.6)';
  };

  return (
    <div className="grid grid-cols-10 gap-1.5">
      {days.map((day, i) => (
        <div
          key={i}
          className="aspect-square rounded-sm"
          style={{ background: getIntensity(day.xp) }}
          title={`${format(day.date, 'MMM d')}: ${day.xp} XP`}
        />
      ))}
    </div>
  );
};

function getLevelTitle(level: number): string {
  if (level <= 5) return 'Novice';
  if (level <= 10) return 'Apprentice';
  if (level <= 15) return 'Scholar';
  if (level <= 20) return 'Adept';
  if (level <= 25) return 'Sage';
  if (level <= 30) return 'Maven';
  return 'Lexicographer';
}

const DashboardSkeleton: React.FC = () => (
  <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-5xl mx-auto space-y-5">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="glass-card p-4 rounded-2xl">
          <div className="h-3 w-16 shimmer rounded mb-3" />
          <div className="h-6 w-24 shimmer rounded" />
        </div>
      ))}
    </div>
    <div className="grid md:grid-cols-3 gap-4">
      <div className="md:col-span-2 glass-card p-6 rounded-2xl h-48">
        <div className="h-6 w-48 shimmer rounded mb-3" />
        <div className="h-4 w-64 shimmer rounded mb-8" />
        <div className="h-10 w-32 shimmer rounded" />
      </div>
      <div className="glass-card p-6 rounded-2xl h-48 flex items-center justify-center">
        <div className="w-20 h-20 rounded-full shimmer" />
      </div>
    </div>
  </div>
);
